
import { defineComponent, ref, computed, onMounted } from "vue";
import KpiSection from "./sections/KpiSection.vue";
import GrowthSection from "./sections/GrowthSection.vue";
import EngagementSection from "./sections/EngagementSection.vue";
import EconomySection from "./sections/EconomySection.vue";
import MonetizationSection from "./sections/MonetizationSection.vue";
import GeographySection from "./sections/GeographySection.vue";
import RetentionSection from "./sections/RetentionSection.vue";
import OpsSection from "./sections/OpsSection.vue";
import { useDashboardSection } from "./composables/useDashboardSection";

const emptyKpis = {
  newUsers: 0, dau: 0, wau: 0, mau: 0, stickiness: null,
  coupons: 0, coinsWagered: 0, revenue: 0, payers: 0,
};

// Bare yyyy-MM-dd, built from explicit local date parts — NOT toISOString(), which
// shifts to UTC first and can silently land on the wrong day, and NOT
// toLocaleDateString("en-CA") either: that happens to yield ISO shape in current
// browsers but depends on ICU locale data being present/correct, so it's not a
// dependable contract. The backend model-binds an offset-bearing value to UTC and
// truncates to date, which would shift the default range a day earlier for any
// timezone west of UTC.
const toDateOnly = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default defineComponent({
  name: "AdminDashboardV2",
  components: {
    KpiSection, GrowthSection, EngagementSection, EconomySection,
    MonetizationSection, GeographySection, RetentionSection, OpsSection,
  },
  setup() {
    const today = new Date();
    // Date arithmetic via setDate, not `today.getTime() - 29 * 86400000` — the
    // fixed-ms subtraction shifts by an hour across a DST boundary and can land on
    // the wrong day.
    const monthAgo = new Date(today);
    monthAgo.setDate(monthAgo.getDate() - 29);

    const fromIso = ref(toDateOnly(monthAgo));
    const toIso = ref(toDateOnly(today));

    const kpiSection = useDashboardSection("kpis", emptyKpis);
    const growthSection = useDashboardSection("growth", {
      newUsers: [], dau: [], completionRate: 0, onboardingFunnel: [],
    });
    const engagementSection = useDashboardSection("engagement", {
      coupons: [], couponsPerActiveUser: null, winRate: 0, avgCoinsPerCoupon: 0,
    });
    const economySection = useDashboardSection("economy", {
      coinsIn: [], coinsOut: 0, netBalance: 0, netPerDay: [],
    });
    const monetizationSection = useDashboardSection("monetization", {
      revenue: [], totalRevenue: 0, payers: 0, arpu: null, arppu: 0, refunds: 0,
    });
    const geographySection = useDashboardSection("geography", { newUsersByCountry: [] });
    const retentionSection = useDashboardSection("retention", { cohorts: [] });
    const opsSection = useDashboardSection("ops", {
      failedTransactions: 0, pendingTransactions: 0, staleOpenCoupons: 0,
    });

    const freshness = computed(() => {
      const at = kpiSection.generatedAt.value;
      if (!at) return "";
      const minutes = Math.round((Date.now() - new Date(at).getTime()) / 60000);
      return minutes < 1 ? "just now" : `${minutes} min ago`;
    });

    async function loadRetention() {
      await retentionSection.load(fromIso.value, toIso.value);
    }

    async function loadOps() {
      await opsSection.load(fromIso.value, toIso.value);
    }

    // Τα KPIs πρώτα, μετά τα υπόλοιπα ελαφριά παράλληλα.
    async function reload() {
      await kpiSection.load(fromIso.value, toIso.value);
      await Promise.all([
        growthSection.load(fromIso.value, toIso.value),
        economySection.load(fromIso.value, toIso.value),
        monetizationSection.load(fromIso.value, toIso.value),
        engagementSection.load(fromIso.value, toIso.value),
        geographySection.load(fromIso.value, toIso.value),
      ]);

      // Τα βαριά ανανεώνονται μόνο αν ο admin τα έχει ήδη δει.
      if (retentionSection.loaded.value) await loadRetention();
      if (opsSection.loaded.value) await loadOps();
    }

    onMounted(reload);

    return {
      fromIso, toIso, freshness, reload, loadRetention, loadOps,
      kpiSection, growthSection, engagementSection, economySection,
      monetizationSection, geographySection, retentionSection, opsSection,
    };
  },
});
